import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../apiServices/axios';
import {
    JOURNAL_ANALYSIS,
    JOURNAL_ANALYSIS_CHAT,
    JOURNAL_ANALYSIS_MESSAGES,
    JOURNAL_ANALYSIS_SEND,
    JOURNAL_ANALYSIS_PROMPTS,
    JOURNAL_ANALYSIS_PROMPT,
} from '../apiServices/api_routes';

const projectId = () => localStorage['project_id'];

export const fetchJournalAnalysisChats = createAsyncThunk('journalAnalysis/fetchChats', async (arg, { rejectWithValue }) => {
    try {
        const res = await axiosInstance.get(JOURNAL_ANALYSIS(projectId()));
        return res.data;
    } catch (err) {
        return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to load analyses');
    }
});

export const createJournalAnalysisChat = createAsyncThunk('journalAnalysis/createChat', async (body = {}, { rejectWithValue }) => {
    try {
        const res = await axiosInstance.post(JOURNAL_ANALYSIS(projectId()), body);
        return res.data;
    } catch (err) {
        return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to create analysis');
    }
});

export const patchJournalAnalysisChat = createAsyncThunk(
    'journalAnalysis/patchChat',
    async ({ chatId, ...body }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.patch(JOURNAL_ANALYSIS_CHAT(projectId(), chatId), body);
            return res.data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to update analysis');
        }
    },
);

export const deleteJournalAnalysisChat = createAsyncThunk('journalAnalysis/deleteChat', async (chatId, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(JOURNAL_ANALYSIS_CHAT(projectId(), chatId));
        return chatId;
    } catch (err) {
        return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to delete analysis');
    }
});

export const fetchJournalAnalysisMessages = createAsyncThunk(
    'journalAnalysis/fetchMessages',
    async (chatId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(JOURNAL_ANALYSIS_MESSAGES(projectId(), chatId));
            return { chatId, messages: res.data?.messages || [] };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to load messages');
        }
    },
);

export const sendJournalAnalysisMessage = createAsyncThunk(
    'journalAnalysis/sendMessage',
    async ({ chatId, message }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post(JOURNAL_ANALYSIS_SEND(projectId(), chatId), { message });
            return res.data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to send message');
        }
    },
);

export const fetchJournalAnalysisPrompts = createAsyncThunk('journalAnalysis/fetchPrompts', async (_, { rejectWithValue }) => {
    try {
        const res = await axiosInstance.get(JOURNAL_ANALYSIS_PROMPTS);
        return res.data;
    } catch (err) {
        return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to load saved prompts');
    }
});

export const createJournalAnalysisPrompt = createAsyncThunk(
    'journalAnalysis/createPrompt',
    async ({ title, prompt }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post(JOURNAL_ANALYSIS_PROMPTS, { title: title || '', prompt });
            return res.data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to save prompt');
        }
    },
);

export const deleteJournalAnalysisPrompt = createAsyncThunk(
    'journalAnalysis/deletePrompt',
    async (promptId, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(JOURNAL_ANALYSIS_PROMPT(promptId));
            return promptId;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.error || err.message || 'Failed to delete prompt');
        }
    },
);

const upsertChat = (chats, chat) => {
    if (!chat?.id) return chats;
    const idx = chats.findIndex(c => c.id === chat.id);
    if (idx === -1) return [chat, ...chats];
    const next = [...chats];
    next[idx] = { ...next[idx], ...chat };
    return next.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
};

const journalAnalysisSlice = createSlice({
    name: 'journalAnalysis',
    initialState: {
        chats: [],
        activeChatId: null,
        messages: [],
        savedPrompts: [],
        listLoading: false,
        messagesLoading: false,
        sending: false,
        promptsLoading: false,
        error: null,
    },
    reducers: {
        setActiveChatId: (state, { payload }) => {
            state.activeChatId = payload;
            if (payload == null) {
                state.messages = [];
            }
        },
        clearJournalAnalysisError: state => {
            state.error = null;
        },
        resetJournalAnalysis: state => {
            state.chats = [];
            state.activeChatId = null;
            state.messages = [];
            state.error = null;
            state.sending = false;
            state.listLoading = false;
            state.messagesLoading = false;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchJournalAnalysisChats.pending, (state, action) => {
                state.listLoading = true;
                state.error = null;
                // Clear immediately so a project switch never flashes the previous project's thread.
                if (action.meta?.arg?.reset) {
                    state.chats = [];
                    state.activeChatId = null;
                    state.messages = [];
                }
            })
            .addCase(fetchJournalAnalysisChats.fulfilled, (state, { payload }) => {
                state.listLoading = false;
                state.chats = Array.isArray(payload) ? payload : payload?.results || [];
                const stillExists = state.chats.some(c => c.id === state.activeChatId);
                if (!stillExists) {
                    state.activeChatId = state.chats[0]?.id ?? null;
                    state.messages = [];
                }
            })
            .addCase(fetchJournalAnalysisChats.rejected, (state, action) => {
                state.listLoading = false;
                state.chats = [];
                state.activeChatId = null;
                state.messages = [];
                state.error = action.payload || 'Failed to load analyses';
            })
            .addCase(createJournalAnalysisChat.fulfilled, (state, { payload }) => {
                state.chats = upsertChat(state.chats, payload);
                state.activeChatId = payload?.id ?? state.activeChatId;
                state.messages = [];
            })
            .addCase(patchJournalAnalysisChat.fulfilled, (state, { payload }) => {
                state.chats = upsertChat(state.chats, payload);
            })
            .addCase(deleteJournalAnalysisChat.fulfilled, (state, { payload }) => {
                state.chats = state.chats.filter(c => c.id !== payload);
                if (state.activeChatId === payload) {
                    state.activeChatId = null;
                    state.messages = [];
                }
            })
            .addCase(fetchJournalAnalysisMessages.pending, state => {
                state.messagesLoading = true;
            })
            .addCase(fetchJournalAnalysisMessages.fulfilled, (state, { payload }) => {
                state.messagesLoading = false;
                if (payload.chatId === state.activeChatId) {
                    state.messages = payload.messages || [];
                }
            })
            .addCase(fetchJournalAnalysisMessages.rejected, (state, action) => {
                state.messagesLoading = false;
                // Stale chat from another project — drop it instead of keeping old messages.
                state.messages = [];
                if (action.meta?.arg != null && action.meta.arg === state.activeChatId) {
                    state.activeChatId = null;
                }
                const status = action.error?.message || '';
                const payload = action.payload || '';
                if (!String(payload).includes('404') && !String(status).includes('404')) {
                    state.error = action.payload || 'Failed to load messages';
                }
            })
            .addCase(sendJournalAnalysisMessage.pending, (state, action) => {
                state.sending = true;
                state.error = null;
                const { chatId, message, tempUserId } = action.meta?.arg || {};
                if (chatId === state.activeChatId && message) {
                    state.messages.push({
                        id: tempUserId || `temp-user-${Date.now()}`,
                        role: 'user',
                        content: message,
                        created_at: new Date().toISOString(),
                        is_temp: true,
                    });
                }
            })
            .addCase(sendJournalAnalysisMessage.fulfilled, (state, { payload, meta }) => {
                state.sending = false;
                const tempUserId = meta?.arg?.tempUserId;
                if (tempUserId) {
                    state.messages = state.messages.filter(m => m.id !== tempUserId);
                } else {
                    state.messages = state.messages.filter(m => !m.is_temp);
                }
                if (payload?.user_message) state.messages.push(payload.user_message);
                if (payload?.ai_response) state.messages.push(payload.ai_response);
                if (payload?.chat) {
                    state.chats = upsertChat(state.chats, payload.chat);
                }
            })
            .addCase(sendJournalAnalysisMessage.rejected, (state, action) => {
                state.sending = false;
                const tempUserId = action.meta?.arg?.tempUserId;
                if (tempUserId) {
                    state.messages = state.messages.filter(m => m.id !== tempUserId);
                } else {
                    state.messages = state.messages.filter(m => !m.is_temp);
                }
                state.error = action.payload || 'Failed to send message';
            })
            .addCase(fetchJournalAnalysisPrompts.pending, state => {
                state.promptsLoading = true;
            })
            .addCase(fetchJournalAnalysisPrompts.fulfilled, (state, { payload }) => {
                state.promptsLoading = false;
                state.savedPrompts = Array.isArray(payload) ? payload : payload?.results || [];
            })
            .addCase(fetchJournalAnalysisPrompts.rejected, (state, action) => {
                state.promptsLoading = false;
                // Prompts are user-global; don't overwrite the main analysis error surface.
                console.warn(action.payload || 'Failed to load prompts');
            })
            .addCase(createJournalAnalysisPrompt.fulfilled, (state, { payload }) => {
                state.savedPrompts = [payload, ...state.savedPrompts.filter(p => p.id !== payload.id)];
            })
            .addCase(deleteJournalAnalysisPrompt.fulfilled, (state, { payload }) => {
                state.savedPrompts = state.savedPrompts.filter(p => p.id !== payload);
            });
    },
});

export const { setActiveChatId, clearJournalAnalysisError, resetJournalAnalysis } = journalAnalysisSlice.actions;
export default journalAnalysisSlice.reducer;
